import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import '../../pages/Credits.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminCreditPurchases = () => {
  const { token } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [filterStatus, setFilterStatus] = useState('all');
  const [selected, setSelected] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const authToken = token || localStorage.getItem('marilink_token');
      const qs = new URLSearchParams({ page, limit: 20 });
      if (filterStatus !== 'all') qs.append('status', filterStatus);
      const res = await fetch(`${API_BASE}/admin/credits/purchases?${qs}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (data.success) {
        setPurchases(data.purchases || []);
        setPagination(data.pagination);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, filterStatus]);

  useEffect(() => { fetchData(1); }, [fetchData]);

  const handleVerify = async (approved) => {
    if (!selected) return;
    if (!approved && !rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    setProcessing(true);
    try {
      const authToken = token || localStorage.getItem('marilink_token');
      const res = await fetch(`${API_BASE}/admin/credits/purchases/${selected._id}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ approved, rejectionReason })
      });
      const data = await res.json();
      if (data.success) {
        alert(approved ? '✅ Credits added to user wallet' : '❌ Purchase rejected');
        setSelected(null);
        setRejectionReason('');
        fetchData(pagination.page);
      } else {
        alert(data.message || 'Failed');
      }
    } catch (e) { alert('Network error'); }
    finally { setProcessing(false); }
  };

  const badgeClass = (s) => ({
    payment_verified: 'completed', payment_uploaded: 'pending',
    pending_payment: 'pending', rejected: 'failed', cancelled: 'cancelled'
  }[s] || 'pending');

  return (
    <div className="cr-page">
      <div className="cr-header">
        <div>
          <h1><i className="fas fa-money-check-alt"></i> Credit Purchases</h1>
          <p>Review and approve credit purchase requests</p>
        </div>
      </div>

      <div className="cr-filters">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="pending_payment">Pending Payment</option>
          <option value="payment_uploaded">Awaiting Review</option>
          <option value="payment_verified">Completed</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <span className="cr-filter-count">
          {pagination.total} purchases
        </span>
      </div>

      {loading ? (
        <div className="cr-loading"><i className="fas fa-spinner fa-spin"></i></div>
      ) : purchases.length === 0 ? (
        <div className="cr-empty">
          <i className="fas fa-inbox"></i>
          <h3>No purchases found</h3>
        </div>
      ) : (
        <div className="cr-table-wrap">
          <table className="cr-table">
            <thead>
              <tr>
                <th>Purchase ID</th>
                <th>User</th>
                <th>Credits</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {purchases.map(p => (
                <tr key={p._id}>
                  <td><span className="cr-tx-id">{p.purchaseId}</span></td>
                  <td>
                    <div className="cr-user-cell">
                      <span className="cr-user-name">{p.userId?.company || p.userId?.name}</span>
                      <span className="cr-user-email">{p.userId?.email}</span>
                    </div>
                  </td>
                  <td><span className="cr-tx-positive">+{p.credits}</span></td>
                  <td>{p.currency} {p.amount}</td>
                  <td><span className={`cr-badge ${badgeClass(p.status)}`}>{p.status.replace(/_/g, ' ')}</span></td>
                  <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button className="cr-btn cr-btn-secondary" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => setSelected(p)}>
                      <i className="fas fa-eye"></i> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="cr-modal-overlay" onClick={() => setSelected(null)}>
          <div className="cr-modal" onClick={e => e.stopPropagation()}>
            <h3><i className="fas fa-receipt"></i> Purchase Details</h3>
            <p className="cr-modal-sub">{selected.purchaseId}</p>

            <div className="cr-bank">
              <div className="cr-bank-row"><span>User</span><span>{selected.userId?.company || selected.userId?.name}</span></div>
              <div className="cr-bank-row"><span>Email</span><span>{selected.userId?.email}</span></div>
              <div className="cr-bank-row"><span>Credits</span><span className="cr-strong-green">{selected.credits}</span></div>
              <div className="cr-bank-row"><span>Amount</span><span>{selected.currency} {selected.amount}</span></div>
              <div className="cr-bank-row"><span>Status</span><span>{selected.status.replace(/_/g, ' ')}</span></div>
            </div>

            {selected.paymentProof?.imageUrl && (
              <div className="cr-proof-block">
                <label className="cr-proof-label">Payment Proof</label>
                <img
                  src={selected.paymentProof.imageUrl}
                  alt="Proof"
                  className="cr-proof-image"
                />
              </div>
            )}

            {selected.status === 'payment_uploaded' && (
              <>
                <div className="cr-form-group" style={{ marginTop: 18 }}>
                  <label>Rejection Reason (if rejecting)</label>
                  <textarea rows="3" value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} placeholder="Reason for rejection..." />
                </div>
                <div className="cr-modal-actions">
                  <button className="cr-btn cr-btn-secondary" onClick={() => setSelected(null)} disabled={processing}>Close</button>
                  <button className="cr-btn cr-btn-danger" onClick={() => handleVerify(false)} disabled={processing}>
                    <i className="fas fa-times"></i> Reject
                  </button>
                  <button className="cr-btn cr-btn-primary" onClick={() => handleVerify(true)} disabled={processing}>
                    {processing ? <><i className="fas fa-spinner fa-spin"></i></> : <i className="fas fa-check"></i>} Approve & Add Credits
                  </button>
                </div>
              </>
            )}
            {selected.status !== 'payment_uploaded' && (
              <div className="cr-modal-actions">
                <button className="cr-btn cr-btn-secondary" onClick={() => setSelected(null)}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCreditPurchases;